// @flow
import * as React from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import styled from 'styled-components'
import type { AppStateType } from '../../reducers/appstate'
import { withRouter } from 'next/router'
import { colors } from '@components/shared/colors'
import { config } from 'react-awesome-styled-grid'
import AccountManagement from '@components/account/AccountManagement'
import { CTAPrimary, GoodSansB1, GoodSansB3, QueensH1 } from '@components/shared'
import type { RefundObjectType } from '../../flowTypes'
import { updateRefundRequest } from '../../actions/subscriptionActions'
import { LinkWithUtms } from '../../actions/navigationActions'

type PropsType = {
  userId?: string,
  isSubscribed: boolean,
  stripePurchaseKey?: string,
  refund: RefundObjectType
}

type DispatchPropsType = {
  updateRefundRequest: (stripePurchaseKey?: string, amountToRefund: number) => Promise<void>
}

class Refund extends React.Component<PropsType & DispatchPropsType> {
  renderRefund (): React.Node {
    const { refund, stripePurchaseKey } = this.props
    if (!refund || !refund.amountPaid) {
      return null
    }
    const amountPaidOrig = refund.amountPaid
    const amountPaid = Math.round(amountPaidOrig / 100 * 100) / 100
    const newPriceAmount = Math.round((amountPaidOrig / 2) / 100 * 100) / 100
    let originalprice = `$${amountPaid}/year`

    if (amountPaid < 15.00) { // monthly
      originalprice = `$${amountPaid} this month`
    }

    let newPrice = `$${newPriceAmount}/year`
    let perMonthPrice = `(only $${Math.round(newPriceAmount / 12 * 100) / 100}/month)`
    if (amountPaid < 15.00) { // monthly
      newPrice = `$${newPriceAmount}`
      perMonthPrice = null
    }
    return (<>
      <LimitedTime><p>Limited Time Offer</p></LimitedTime>
      <PromotionalHeader>Partial Refund <br/><span>50% off</span></PromotionalHeader>
      <LightBlueDiv>
        <PriceWhite className="strike">{originalprice}</PriceWhite>
        <PriceOrange>{newPrice}</PriceOrange>
        <PriceWhite>{perMonthPrice}</PriceWhite>
      </LightBlueDiv>
      <TermsInfo>
        <p>You are eligible for a partial refund of ${newPriceAmount}! We'll cancel your subscription so it won’t renew,
          but you can keep it for the rest of the original subscription period. 100% refunds will immediately end your unlimited listening subscription.</p>
      </TermsInfo>
      <ButtonsContainer>
        <PrimaryCta
          // $FlowFixMe
          onClick={(): void => this.props.updateRefundRequest(stripePurchaseKey, amountPaidOrig / 2)}
        >
         GET 50% REFUND
        </PrimaryCta>
        <SecondaryCta
          // $FlowFixMe
          onClick={(): void => this.props.updateRefundRequest(stripePurchaseKey, amountPaidOrig)}
          showHover={true}
        >
         No thanks, please give me a 100% refund
        </SecondaryCta>
      </ButtonsContainer>
    </>)
  }

  renderSuccess (): React.Node {
    const { refund } = this.props
    return (<>
      <PromotionalHeader>Refund Processed</PromotionalHeader>
      <TermsInfo>
        <p>You've been refunded ${(refund.amountToRefund || 0) / 100}. It can take up to 7 days for the refund to be processed by your bank.</p>
      </TermsInfo>
      <LinkWithUtms pathname={'/account'}>
        <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
      </LinkWithUtms>
    </>)
  }

  renderProcess (): React.Node {
    return (<>
      <PromotionalHeader>Refund Processing</PromotionalHeader>
      <TermsInfo>
        <p>Processing your refund. Give us a second!</p>
      </TermsInfo>
      <LinkWithUtms pathname={'/account'}>
        <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
      </LinkWithUtms>
    </>)
  }

  renderFailure (): React.Node {
    const { refund } = this.props
    return (<>
      <PromotionalHeader>Refund Error</PromotionalHeader>
      <TermsInfo>
        <p>{refund?.message}</p>
      </TermsInfo>
      <LinkWithUtms pathname={'/account'}>
        <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
      </LinkWithUtms>
    </>)
  }

  renderIneligible (): React.Node {
    const { refund } = this.props
    return (<>
      <PromotionalHeader>Ineligible for Refund</PromotionalHeader>
      <TermsInfo>
        <p>{refund?.message}</p>
      </TermsInfo>
      <LinkWithUtms pathname={'/account'}>
        <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
      </LinkWithUtms>
    </>)
  }

  renderNoWebSubFound (): React.Node {
    return (<>
      <PromotionalHeader>Ineligible for Refund</PromotionalHeader>
      <TermsInfo>
        <p>We were unable to find a subscription to refund. Please reach out to customer support.</p>
      </TermsInfo>
      <LinkWithUtms pathname={'/account'}>
        <CTAPrimary>BACK TO ACCOUNT</CTAPrimary>
      </LinkWithUtms>
    </>)
  }

  renderStatus (): React.Node {
    const { refund, stripePurchaseKey } = this.props
    if (refund && refund.status === 'eligible') {
      return this.renderRefund()
    } else if (refund && refund.status === 'succeeded') {
      return this.renderSuccess()
    } else if (refund && refund.status === 'failed') {
      return this.renderFailure()
    } else if (refund && refund.status === 'processRefund') {
      return this.renderProcess()
    } else if (refund && refund.status === 'ineligible') {
      return this.renderIneligible()
    } else if (!stripePurchaseKey) {
      return this.renderNoWebSubFound()
    }
    return null
  }

  render (): React.Node {
    return (
      <AccountManagement title={'Dipsea | Refund'} pageDescription={'30-day no questions asked refunds'}>
        <FlexCol>
          {this.renderStatus()}
        </FlexCol>
      </AccountManagement>
    )
  }
}

const mapStateToProps = (state: AppStateType): any => {
  return {
    userId: state.user?.id,
    stripePurchaseKey: state.user?.stripe_purchase_key,
    isSubscribed: state.user?.isSubscribed || false,
    refund: state.refund
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
    updateRefundRequest: (stripePurchaseKey?: string, amountToRefund: number): Promise<void> => {
      return updateRefundRequest(stripePurchaseKey, amountToRefund)
    }
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Refund))

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

export const PrimaryCta = styled.button`
  border: none;
  background-color: ${colors.orange};
  border-radius: 32px;
  color: ${colors.white};
  cursor: pointer;
  font-family: 'Good Sans Bold';
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  height: 40px;
  margin-top: 16px;
  outline: none;
  padding: 12px 32px;

  ${(props: any): any => {
  if (props.disabled) {
    return `opacity: 0.5;`
  } else if (props.showHover) {
    return `
        :focus { box-shadow: 0 0 0 2pt ${colors.lightPurple}; }
        :hover { opacity: 0.5;}
      `
  }
}}
`

export const SecondaryCta = styled(PrimaryCta)`
  border: 1px solid ${colors.orange};
  color: ${colors.orange};
  background-color: ${colors.midnightBlue};
`

export const PromotionalHeader = styled.h1`
  ${QueensH1};
  color: ${colors.white};
  text-align: center;
  margin-top: 16px;

  ${(props: any): string => config(props).media.md`
    font-size: 44px;
    line-height: 53px;
  `}

  ${(props: any): string => config(props).media.lg`
    font-size: 45px;
    line-height: 54px;
  `}

  ${(props: any): string => config(props).media.xl`
    font-size: 45px;
    line-height: 54px;
  `}

  span {
    color: ${colors.tintOrange};
  }
`

export const LightBlueDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  width: 375px;
  height: 135px;

  background-color: rgba(55, 44, 188, 1);
  border-radius: 10px;
  margin-top: 16px;
  padding-top: 10px;
  padding-bottom: 10px;
`

export const PriceWhite = styled.p`
  ${GoodSansB1};
  font-size: 14px;
  color: ${colors.white70};
  &.strike {
    text-decoration: line-through;
  }
  @media (min-width: 1024px) {
    font-size: 14px;
  }
`

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  width: 100%;
`

const LimitedTime = styled.div`
  background: none;
  border: 1px solid ${colors.white50};
  padding: 0;
  outline: none;
  font-weight: normal;
  border-radius: 14px;
  padding: 6px;

  ${(props: any): string => config(props).media.md`
    border-radius: 15px;
  `}

  ${(props: any): string => config(props).media.lg`
    border-radius: 20px;
  `}

  p {
    ${GoodSansB3};
    color: ${colors.white};
    opacity: 1;
    margin: 0;
    font-weight: normal;

    padding: 0 8px;

    ${(props: any): string => config(props).media.lg`
    padding: 0 12px;
    `}
  }

  ${(props: any): ?string =>
  props.showHover &&
  `:hover {
      background-color: ${colors.orange};
    }`}
`
export const PriceOrange = styled.h1`
  ${QueensH1};
  color: ${colors.orange90};
  text-align: center;

  ${(props: any): string => config(props).media.md`
    font-size: 44px;
    line-height: 53px;
    `}

  ${(props: any): string => config(props).media.lg`
    font-size: 45px;
    line-height: 54px;
    `}


  ${(props: any): string => config(props).media.xl`
    font-size: 45px;
    line-height: 54px;
    `}
`

export const TermsInfo = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  p {
    ${GoodSansB3};
    font-size: 16px;
    text-align: center;
    color: ${colors.white70};
    opacity: 1;
    margin: 0;

    ${(props: any): string => config(props).media.lg`
    font-size: 16px;
      padding: 0 12px;
    `}
  }
`
